import { AxiosError, HttpStatusCode } from 'axios';

/**
 * Codes d'erreur applicatifs utilisés dans toute l'application.
 * Chaque code correspond à un cas d'erreur HTTP ou réseau identifié.
 */
export enum HttpErrorCode {
  NETWORK_ERROR = 'NETWORK_ERROR',
  TIMEOUT = 'TIMEOUT',
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',
  NOT_FOUND = 'NOT_FOUND',
  CONFLICT = 'CONFLICT',
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  SERVER_ERROR = 'SERVER_ERROR',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Erreur HTTP applicative normalisée.
 * Encapsule une AxiosError brute en une erreur typée avec un code, un message
 * utilisateur lisible, le statut HTTP d'origine et les données serveur éventuelles.
 */
export class AppHttpError extends Error {
  public readonly code: HttpErrorCode;
  public readonly statusCode: number | null;
  public readonly serverData: unknown;
  public readonly originalError: AxiosError;

  constructor(
    code: HttpErrorCode,
    message: string,
    statusCode: number | null,
    originalError: AxiosError,
    serverData?: unknown,
  ) {
    super(message);
    this.name = 'AppHttpError';
    this.code = code;
    this.statusCode = statusCode;
    this.originalError = originalError;
    this.serverData = serverData;
  }
}

/** Messages utilisateur par défaut associés à chaque code d'erreur. */
const DEFAULT_MESSAGES: Record<HttpErrorCode, string> = {
  [HttpErrorCode.NETWORK_ERROR]:
    'Impossible de contacter le serveur. Vérifiez votre connexion internet.',
  [HttpErrorCode.TIMEOUT]: 'La requête a pris trop de temps. Veuillez réessayer.',
  [HttpErrorCode.UNAUTHORIZED]: 'Votre session a expiré. Veuillez vous reconnecter.',
  [HttpErrorCode.FORBIDDEN]: "Vous n'avez pas les droits nécessaires pour effectuer cette action.",
  [HttpErrorCode.NOT_FOUND]: 'La ressource demandée est introuvable.',
  [HttpErrorCode.CONFLICT]: 'Un conflit a été détecté. La ressource existe peut-être déjà.',
  [HttpErrorCode.VALIDATION_ERROR]: 'Les données envoyées sont invalides.',
  [HttpErrorCode.SERVER_ERROR]: 'Une erreur interne du serveur est survenue. Veuillez réessayer.',
  [HttpErrorCode.UNKNOWN]: 'Une erreur inattendue est survenue.',
};

/**
 * Détermine le HttpErrorCode à partir du statut HTTP.
 */
function mapStatusToCode(status: number): HttpErrorCode {
  switch (status) {
    case HttpStatusCode.Unauthorized:
      return HttpErrorCode.UNAUTHORIZED;
    case HttpStatusCode.Forbidden:
      return HttpErrorCode.FORBIDDEN;
    case HttpStatusCode.NotFound:
      return HttpErrorCode.NOT_FOUND;
    case HttpStatusCode.Conflict:
      return HttpErrorCode.CONFLICT;
    case HttpStatusCode.BadRequest:
    case HttpStatusCode.UnprocessableEntity:
      return HttpErrorCode.VALIDATION_ERROR;
    default:
      return status >= 500 ? HttpErrorCode.SERVER_ERROR : HttpErrorCode.UNKNOWN;
  }
}

/** Messages HTTP génériques renvoyés par les serveurs, à ignorer au profit des defaults français. */
const GENERIC_HTTP_MESSAGES = new Set([
  'bad request',
  'unauthorized',
  'forbidden',
  'not found',
  'method not allowed',
  'conflict',
  'unprocessable entity',
  'internal server error',
  'bad gateway',
  'service unavailable',
  'gateway timeout',
]);

/**
 * Extrait un message lisible depuis la réponse serveur si disponible.
 * Ignore les messages HTTP génériques (ex: "Forbidden", "Not Found").
 */
function extractServerMessage(data: unknown): string | undefined {
  let candidate: string | undefined;

  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    if (typeof obj.message === 'string') candidate = obj.message;
    else if (typeof obj.error === 'string') candidate = obj.error;
  } else if (typeof data === 'string' && data.length > 0 && data.length < 300) {
    candidate = data;
  }

  if (!candidate) return undefined;
  // Ignorer les messages HTTP génériques pour garder les defaults français
  if (GENERIC_HTTP_MESSAGES.has(candidate.trim().toLowerCase())) return undefined;
  return candidate;
}

/**
 * Point d'entrée principal : transforme n'importe quelle erreur Axios
 * en une AppHttpError normalisée et typée.
 *
 * @example
 * ```ts
 * // Usage simple (messages par défaut)
 * try {
 *   await axiosInstance.get('/api/resource');
 * } catch (error) {
 *   throw normalizeHttpError(error);
 * }
 *
 * // Usage avec messages personnalisés
 * try {
 *   await axiosInstance.delete(`/api/emprunts/${id}`);
 * } catch (error) {
 *   throw normalizeHttpError(error, {
 *     [HttpErrorCode.NOT_FOUND]: 'Emprunt introuvable.',
 *     [HttpErrorCode.CONFLICT]: 'Cet emprunt ne peut pas être supprimé.',
 *   });
 * }
 *
 * // Usage avec message par défaut (catch-all)
 * throw normalizeHttpError(error, {
 *   [HttpErrorCode.SERVER_ERROR]: 'Échec spécifique.',
 * }, 'Message générique pour tous les autres cas.');
 * ```
 */
export function normalizeHttpError(
  error: unknown,
  customMessages?: Partial<Record<HttpErrorCode, string>>,
  defaultMessage?: string,
): AppHttpError {
  // Si déjà normalisée et qu'on a un message personnalisé pour ce code, on le remplace
  if (error instanceof AppHttpError) {
    const custom = customMessages?.[error.code] ?? defaultMessage;
    if (custom) {
      return new AppHttpError(
        error.code,
        custom,
        error.statusCode,
        error.originalError,
        error.serverData,
      );
    }
    return error;
  }

  const axiosError = error as AxiosError;

  // Cas 1 : Erreur réseau (pas de réponse du serveur)
  if (!axiosError.response) {
    const isTimeout = axiosError.code === 'ECONNABORTED' || axiosError.message?.includes('timeout');
    const code = isTimeout ? HttpErrorCode.TIMEOUT : HttpErrorCode.NETWORK_ERROR;
    const message = customMessages?.[code] ?? defaultMessage ?? DEFAULT_MESSAGES[code];
    return new AppHttpError(code, message, null, axiosError);
  }

  // Cas 2 : Réponse HTTP avec un statut d'erreur
  const { status, data } = axiosError.response;
  const code = mapStatusToCode(status);
  const serverMessage = extractServerMessage(data);
  const message =
    customMessages?.[code] ?? defaultMessage ?? serverMessage ?? DEFAULT_MESSAGES[code];

  return new AppHttpError(code, message, status, axiosError, data);
}

/**
 * Vérifie si une erreur est une AppHttpError avec un code spécifique.
 */
export function isHttpError(error: unknown, code?: HttpErrorCode): error is AppHttpError {
  if (!(error instanceof AppHttpError)) return false;
  return code === undefined || error.code === code;
}
